import React, { Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Components

import PageHeader from '@components/ssap/headers/PageHeader';
import NamesInputs from '@components/ssap/pages/pageIncludes/common/names/NamesInputs';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// ActionCreators / Selectors

import { changeData } from '@actions/ssap/ssapActionCreators';
import { getSsapData } from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { omit } from '@utils/miscellaneous/objectHelpers';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { PHONE_TYPES } from '@constants/ssap/miscellaneous/miscellaneous';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';



const EmergencyContact = props => {

    const {
        isNameWarningShown
    } = props;

    const cleanedProps = omit(props, [ 'children', 'isNameWarningShown' ]);

    const data = useSelector(getSsapData);

    const {
        taxHousehold: {
            householdMember: householdMembers
        }
    } = data;

    const primaryContact = householdMemberTools.getPrimaryContact(householdMembers);

    const {
        emergencyContactInfo: {
            name: {
                firstName,
                middleName,
                lastName,
                suffix
            },
            phone: {
                phoneNumber
            },
            emailAddress
        }
    } = primaryContact;

    const dispatch = useDispatch();

    const handlePhoneChange = (...args) => {

        const [ , , , [ , value ] ] = args;

        const newState = cloneObject(data);

        newState
            .taxHousehold
            .householdMember = householdMemberTools.updatePrimaryContact(householdMembers, {
                ...primaryContact,
                emergencyContactInfo: {
                    ...primaryContact.emergencyContactInfo,
                    phone: {
                        ...primaryContact.emergencyContactInfo.phone,
                        phoneNumber: value ? value : null,
                        phoneType: value ? PHONE_TYPES.HOME : null
                    }
                }
            });

        dispatch(changeData(newState));
    };

    const onFieldChange = (...args) => {

        const newState = cloneObject(data);

        const  [newData ] = args;

        newState.taxHousehold.householdMember = householdMemberTools
            .updatePrimaryContact(newState.taxHousehold.householdMember, { ...newData });

        dispatch(changeData(newState));
    };

    return (
        <Fragment>
            <PageHeader
                text = 'ssap.subheader.emergency_contact_info'
                headerClassName = 'gi-heading-alt'
            />

            <ValidationErrorMessage
                isVisible = { isNameWarningShown }
                messageText = { VALIDATION_ERROR_MESSAGES.BOTH_NAMES }
            />

            <NamesInputs
                { ...{
                    firstName,
                    middleName,
                    lastName,
                    suffix,
                    areAllFieldsMixed: true,
                    currentData: primaryContact,
                    fields: [ 'emergencyContactInfo', 'name' ],
                    isRequired: false,
                    inputActions: {
                        onFieldChange
                    },
                    ...cleanedProps
                } }
            />

            <UniversalInput
                label = { {
                    text: 'ssap.input.label.home_phone',
                    replacements: { alt: false },
                    labelClasses: []
                } }
                inputData = { {
                    isInputRequired: false,
                    type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                    name: 'phoneNumber',
                    helperClasses: [],
                    value: phoneNumber,
                    placeholder: 'ssap.input.placeholder.phone_number',
                    validationType: VALIDATION_TYPES.PHONE_NUMBER,
                    areAllFieldsMixed: true,
                    currentData: primaryContact,
                    fields: [ 'emergencyContactInfo', 'phone' ],
                    isInputStandalone: true
                } }
                inputActions = { {
                    onFieldChange: handlePhoneChange
                } }
                { ...cleanedProps }
            />

            <UniversalInput
                label = { {
                    text: 'ssap.input.label.email',
                    labelClasses: []
                } }
                inputData = { {
                    isInputRequired: false,
                    type: INPUT_TYPES.REGULAR_INPUT,
                    name: 'emailAddress',
                    helperClasses: [],
                    value: emailAddress,
                    placeholder: 'ssap.input.placeholder.email',
                    validationType: VALIDATION_TYPES.EMAIL,
                    currentData: primaryContact,
                    fields: [ 'emergencyContactInfo' ],
                    isInputStandalone: true
                } }
                inputActions = { {
                    onFieldChange
                } }
                { ...cleanedProps }
            />
        </Fragment>
    );
};

export default EmergencyContact;